import { useEffect, useState } from "react";

type Options = {
    /**
     * Висота фіксованого хедера (px), щоб коректно визначати, що «секція вгорі».
     * Напр., 80 або 100 пікселів — залежить від твого #header у scrolled-стані.
     */
    headerOffset?: number;
    /**
     * Частка секції, яка має бути у видимій області, щоб вважати її активною.
     * 0.5 = 50% висоти секції.
     */
    threshold?: number;
};

export function useScrollSpy(sectionIds: string[], options: Options = {}) {
    const { headerOffset = 100, threshold = 0.5 } = options;
    const [activeId, setActiveId] = useState<string>("");

    useEffect(() => {
        const elements = sectionIds
            .map((id) => document.getElementById(id))
            .filter((el): el is HTMLElement => !!el);

        if (elements.length === 0) return;

        // root = viewport; rootMargin зміщує «верх» на висоту хедера
        const observer = new IntersectionObserver(
            (entries) => {
                // Знаходимо ту, що найбільш видима / перетинається
                // Сортуємо за площиною перетину (intersectionRatio) та позицією
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visible.length > 0) {
                    const topMost = visible[0].target as HTMLElement;
                    setActiveId(topMost.id);
                    return;
                }

                // Якщо жодна не перетинається (перехід між секціями),
                // можна евристично вибрати найближчу до верхнього краю:
                const byTop = [...entries].sort(
                    (a, b) =>
                        Math.abs(a.target.getBoundingClientRect().top - headerOffset) -
                        Math.abs(b.target.getBoundingClientRect().top - headerOffset)
                );
                const candidate = byTop[0]?.target as HTMLElement | undefined;
                if (candidate) setActiveId(candidate.id);
            },
            {
                root: null, // viewport
                rootMargin: `-${headerOffset}px 0px 0px 0px`,
                threshold: buildThresholdList(threshold),
            }
        );

        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [sectionIds.join("|")]); // якщо зміниться набір id

    return activeId;
}

// Допоміжна: генеруємо масив порогів для стабільнішого спрацьовування
function buildThresholdList(main: number) {
    const steps = [0, main, 1];
    // Приберемо дублікати та вийдемо в зростаючому порядку
    return Array.from(new Set(steps)).sort((a, b) => a - b);
}
